import { earliestRetainedDay, presetExceedsRetention, RANGE_PRESETS, type RangePreset } from '../ranges';

export function RangePicker({
  value,
  onChange,
  custom,
  onCustomChange,
  presets,
  maxLookbackDays,
}: {
  value: RangePreset;
  onChange: (p: RangePreset) => void;
  custom: { from: string; to: string };
  onCustomChange: (custom: { from: string; to: string }) => void;
  /** Restrict the choices (e.g. the Executive view hides Day and Week). Defaults to all presets. */
  presets?: RangePreset[];
  /** Days of data the workspace retains. Presets reaching further back are disabled; undefined offers all. */
  maxLookbackDays?: number;
}) {
  const shown = presets ? RANGE_PRESETS.filter((p) => presets.includes(p.key)) : RANGE_PRESETS;
  const today = new Date().toISOString().slice(0, 10);
  const earliest = maxLookbackDays === undefined ? undefined : earliestRetainedDay(maxLookbackDays);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div role="group" aria-label="Time range" className="flex gap-0.5 rounded-input border border-border bg-surface-alt p-0.5">
        {shown.map((p) => {
          // Beyond retention the data is simply gone: offering the range would show a truncated period as if complete.
          const beyond = presetExceedsRetention(p.key, maxLookbackDays);
          return (
            <button
              key={p.key}
              type="button"
              aria-pressed={value === p.key}
              disabled={beyond}
              title={beyond ? `Unavailable: only the last ${maxLookbackDays} days of data are retained` : undefined}
              onClick={() => onChange(p.key)}
              className={`min-h-[32px] rounded px-3 py-1.5 text-sm font-medium tracking-[0.2px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-blue disabled:cursor-not-allowed disabled:opacity-50 ${
                value === p.key ? 'bg-primary-blue text-text-inverse' : `text-text-muted ${beyond ? '' : 'hover:text-text-default'}`
              }`}
            >
              {p.label}
            </button>
          );
        })}
      </div>
      {value === 'C' && (
        <div className="flex items-center gap-1.5 text-xs text-text-muted">
          <label htmlFor="rangeFrom">From</label>
          <input
            id="rangeFrom"
            type="date"
            className="rounded-input border border-border bg-surface px-2 py-1 text-text-default"
            value={custom.from.slice(0, 10)}
            min={earliest}
            max={today}
            onChange={(e) => onCustomChange({ ...custom, from: new Date(e.target.value).toISOString() })}
          />
          <label htmlFor="rangeTo">To</label>
          <input
            id="rangeTo"
            type="date"
            className="rounded-input border border-border bg-surface px-2 py-1 text-text-default"
            value={custom.to.slice(0, 10)}
            min={earliest}
            max={today}
            onChange={(e) => onCustomChange({ ...custom, to: new Date(e.target.value).toISOString() })}
          />
        </div>
      )}
    </div>
  );
}
